import { useState } from 'react'
import {
  useGetPayoutSummaryQuery,
  useCreatePayoutMutation,
  useGetWebhookEarningsQuery,
} from '../../store/api/agentsApi'
import type { PayoutSummaryRow } from '../../store/api/agentsApi'
import { useAdminGetBankDetailsQuery } from '../../store/api/developerApi'
import { Button, Alert, TextInput, Textarea, DateRangePicker, StatCards, Modal, toYMD } from '../../components/ui'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { api, getErrorMessage } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

// Unlocks can come from any agent's answer — webhook (external) agents are the
// main source, but tag the others so the per-agent total still reconciles
// with the "Ekkam Earnings (all-time)" card.
const TYPE_LABEL: Record<string, string> = { external: 'Webhook', hosted: 'RAG', internal: 'Built-in' }

const fmtRupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ── Record payout modal ──────────────────────────────────────────
function RecordPayoutModal({ developer, onClose }: { developer: PayoutSummaryRow; onClose: () => void }) {
  const [amount, setAmount] = useState(developer.outstandingPaise > 0 ? (developer.outstandingPaise / 100).toFixed(2) : '')
  const [amountUsd, setAmountUsd] = useState('')
  const [from, setFrom] = useState(toYMD(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)))
  const [to, setTo]     = useState(toYMD(new Date()))
  const [note, setNote] = useState('')
  const [error, setError] = useState('')
  const [createPayout, { isLoading }] = useCreatePayoutMutation()
  const { data: bankData, isFetching: bankFetching } = useAdminGetBankDetailsQuery(developer.developerId)
  const bank = bankData?.bankDetails ?? null

  const rupees = parseFloat(amount)
  const enteredPaise = !isNaN(rupees) ? Math.round(rupees * 100) : 0
  // Not a hard block — this is a manual correction tool, an admin may legitimately
  // need to record a payout larger than what's currently tracked as outstanding.
  // Just make sure it's not an accidental typo before it's logged.
  const exceedsOutstanding = enteredPaise > 0 && enteredPaise > developer.outstandingPaise

  const usdValue = amountUsd.trim() ? parseFloat(amountUsd) : NaN
  const usdError = amountUsd.trim() && (isNaN(usdValue) || usdValue <= 0)

  const handleSubmit = async () => {
    setError('')
    if (isNaN(rupees) || rupees <= 0) { setError('Enter a valid amount'); return }
    if (usdError) { setError('Enter a valid USD amount, or leave it blank'); return }
    if (exceedsOutstanding) {
      const proceed = window.confirm(
        `This is ${fmtRupees(enteredPaise - developer.outstandingPaise)} more than the ${fmtRupees(developer.outstandingPaise)} currently outstanding for ${developer.developerName}. Record it anyway?`
      )
      if (!proceed) return
    }
    try {
      await createPayout({
        developerId: developer.developerId,
        amountPaise: Math.round(rupees * 100),
        amountUsd: !isNaN(usdValue) ? usdValue : undefined,
        periodFrom: from,
        periodTo: to,
        note: note.trim() || undefined,
      }).unwrap()
      onClose()
    } catch (err: any) {
      setError(err?.data?.message ?? 'Failed to record payout')
    }
  }

  return (
    <Modal onClose={onClose} maxWidth="480px">
      <h2 style={{ margin: '0 0 0.25rem', fontSize: '1.05rem', color: 'var(--text-primary)' }}>Record Payout</h2>
      <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
        For <strong style={{ color: 'var(--text-primary)' }}>{developer.developerName}</strong> — this only logs that you paid them
        outside the app (e.g. bank transfer). No money actually moves.
      </p>

      <div style={{ marginBottom: '1rem', padding: '0.75rem 0.85rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>Bank Details</div>
        {bankFetching && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading…</div>}
        {!bankFetching && !bank && (
          <div style={{ fontSize: '0.8rem', color: 'var(--warning)' }}>⚠️ This developer hasn't added bank details yet — you'll need to reach out to them directly.</div>
        )}
        {!bankFetching && bank && bank.decryptFailed && (
          <div style={{ fontSize: '0.8rem', color: 'var(--error)' }}>
            ⚠️ Bank details are on file but couldn't be read (account number ends {bank.accountNumber.match(/\d+$/)?.[0] ?? '????'}) — this usually means the data was saved under a different server key. Reach out to the developer directly, or check the server encryption config.
          </div>
        )}
        {!bankFetching && bank && !bank.decryptFailed && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.6rem', fontSize: '0.82rem' }}>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Account Holder</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.accountHolderName}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Account Number</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.accountNumber}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>IFSC Code</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.ifscCode}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>Bank Name</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.bankName}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>PAN Number</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{bank.panNumber ?? '—'}</div>
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem' }}>Amount (₹)</label>
            <TextInput value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" type="number" min="0" step="0.01" style={{ width: '100%', boxSizing: 'border-box' }} />
            <span style={{ fontSize: '0.7rem', color: exceedsOutstanding ? 'var(--warning)' : 'var(--text-muted)' }}>Outstanding: {fmtRupees(developer.outstandingPaise)}</span>
            {exceedsOutstanding && (
              <div style={{ fontSize: '0.72rem', color: 'var(--warning)', marginTop: '0.3rem' }}>
                ⚠️ This exceeds the outstanding balance by {fmtRupees(enteredPaise - developer.outstandingPaise)}.
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem' }}>
              Amount $ <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— optional</span>
            </label>
            <TextInput value={amountUsd} onChange={e => setAmountUsd(e.target.value)} placeholder="e.g. 49.00" type="number" min="0" step="0.01" style={{ width: '100%', boxSizing: 'border-box' }} />
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Reference only.</span>
          </div>
        </div>
        <div>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem' }}>Period covered</label>
          <DateRangePicker from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
        </div>
        <div>
          <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.3rem' }}>Note (optional)</label>
          <Textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="e.g. bank transfer reference / UTR" style={{ width: '100%', boxSizing: 'border-box' }} />
        </div>
        {error && <Alert variant="error">{error}</Alert>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.25rem' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => void handleSubmit()} loading={isLoading}>Record Payout</Button>
        </div>
      </div>
    </Modal>
  )
}

// ── Main tab ──────────────────────────────────────────────────────
export function AdminPayouts() {
  const { data: summaryData, isFetching: summaryFetching, refetch: refetchSummary } = useGetPayoutSummaryQuery()
  const { data: earningsData, isFetching: earningsFetching, refetch: refetchEarnings } = useGetWebhookEarningsQuery()
  const [payoutTarget, setPayoutTarget] = useState<PayoutSummaryRow | null>(null)
  const { accessToken } = useAppSelector((s) => s.auth)
  const [exportState, setExportState] = useState<'idle' | 'loading' | 'error'>('idle')

  const summary = summaryData?.summary ?? []
  const byAgent = earningsData?.byAgent ?? []

  const handleExport = async () => {
    setExportState('loading')
    try {
      const { blob, filename } = await api.downloadFile('payments', ENDPOINTS.monetization.adminPayoutsExport, accessToken)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename ?? 'ekkam-payouts-due.xlsx'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      setExportState('idle')
    } catch (err) {
      setExportState('error')
      window.alert(getErrorMessage(err, 'Failed to export payouts'))
      setTimeout(() => setExportState('idle'), 3000)
    }
  }

  const totals = summary.reduce(
    (acc, r) => ({
      earned: acc.earned + r.totalEarnedPaise,
      paid: acc.paid + r.totalPaidPaise,
      outstanding: acc.outstanding + r.outstandingPaise,
    }),
    { earned: 0, paid: 0, outstanding: 0 },
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>💵 Webhook Payouts</div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Button size="sm" variant="ghost" onClick={() => void handleExport()} loading={exportState === 'loading'}>
            📥 Export
          </Button>
          <AdminRefreshBtn onClick={() => { void refetchSummary(); void refetchEarnings() }} loading={summaryFetching || earningsFetching} />
        </div>
      </div>
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        No payment gateway is wired up — payouts happen manually outside the app. Record them here so earned/paid/outstanding
        stays accurate regardless of which date range you're viewing the Monetization report for.
      </p>

      <StatCards
        style={{ marginBottom: '1.25rem' }}
        stats={[
          { icon: '💰', label: 'Total Earned (all-time)', value: fmtRupees(totals.earned) },
          { icon: '✅', label: 'Total Paid Out',           value: fmtRupees(totals.paid) },
          { icon: '⏳', label: 'Total Outstanding',        value: fmtRupees(totals.outstanding) },
          { icon: '🏦', label: 'Ekkam Earnings (all-time)', value: fmtRupees(summaryData?.platformEarningsPaise ?? 0) },
        ]}
      />

      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Owed to creators</div>
      <div className={s.listCard} style={{ marginBottom: '1.5rem' }}>
        {summaryFetching && summary.length === 0 && <div className={s.listCardLoading}>Loading…</div>}
        {!summaryFetching && summary.length === 0 && <div className={s.listCardEmpty}>No developer earnings yet.</div>}
        {summary.map((row, i) => (
          <div key={row.developerId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < summary.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>{row.developerName}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Earned {fmtRupees(row.totalEarnedPaise)} · Paid {fmtRupees(row.totalPaidPaise)}</div>
            </div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', flexShrink: 0, color: row.outstandingPaise > 0 ? 'var(--warning)' : row.outstandingPaise < 0 ? 'var(--error)' : 'var(--success)' }}>
              {fmtRupees(row.outstandingPaise)}
            </div>
            <Button
              size="sm"
              onClick={() => setPayoutTarget(row)}
              disabled={!row.hasBankDetails}
              title={row.hasBankDetails ? undefined : 'This developer has not added bank details yet'}
            >
              Record Payout
            </Button>
          </div>
        ))}
      </div>

      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-primary)', marginBottom: '0.2rem' }}>Ekkam earnings by agent</div>
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
        All-time unlock revenue per agent, ranked by Ekkam's cut (amount on the right). Creator share shown for reference.
      </p>
      <div className={s.listCard} style={{ marginBottom: '1.5rem' }}>
        {earningsFetching && byAgent.length === 0 && <div className={s.listCardLoading}>Loading…</div>}
        {!earningsFetching && byAgent.length === 0 && <div className={s.listCardEmpty}>No unlock earnings yet.</div>}
        {byAgent.map((row, i) => (
          <div key={row.agentId} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 1rem', borderBottom: i < byAgent.length - 1 ? '1px solid var(--border)' : 'none', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 160px', minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                {row.agentIcon ? `${row.agentIcon} ` : ''}{row.agentName}
                {row.agentType !== 'external' && (
                  <span style={{ marginLeft: '0.4rem', fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-muted)', border: '1px solid var(--border)', borderRadius: 4, padding: '0 0.3rem' }}>
                    {TYPE_LABEL[row.agentType] ?? row.agentType}
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                {row.developerName ?? 'No developer'} · {row.unlockCount} unlock{row.unlockCount === 1 ? '' : 's'} · Revenue {fmtRupees(row.totalPaise)} · Creator {fmtRupees(row.agentPaise)}
              </div>
            </div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', flexShrink: 0, color: 'var(--success)' }}>
              {fmtRupees(row.platformPaise)}
            </div>
          </div>
        ))}
      </div>

      {payoutTarget && <RecordPayoutModal developer={payoutTarget} onClose={() => setPayoutTarget(null)} />}
    </div>
  )
}
